import React from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, Typography } from '@mui/material'
import { FormInput } from '@/components/form'
import { getChangePhoneSchema, IUser, User } from '@/@data'
import PhoneCodeSelection from '@/components/selection/PhoneCodeSelection'
import { useTranslator } from '@/hooks/useTranslator'
import { useNotification } from '@/hooks/useNotification'
import { EmployeeConfirmPhoneDialog } from '@/components/user/employee/EmployeeConfirmPhoneDialog'

interface EmployeeEditPhoneFormProps {
  employee?: IUser
  onSuccess?: () => void
  refetch?: () => void
}

const EmployeeEditPhoneForm = React.forwardRef<{ submit: () => void } | null, EmployeeEditPhoneFormProps>(
  ({ employee, refetch, onSuccess }, ref) => {
    const { __ } = useTranslator()
    const n = useNotification()
    const [openConfirm, setOpenConfirm] = useState(false)

    const methods = useForm<any>({
      defaultValues: {
        phone_code: employee?.phone_code || '+84',
        phone: employee?.phone || '',
        phone_new: ''
      },
      resolver: yupResolver(getChangePhoneSchema(__)),
      mode: 'onChange'
    })

    const onSubmit = async (data: any) => {
      if (!employee) return

      try {
        // Construct payload with status = active
        const payload = {
          ...data,
          phone: data.phone_new
        }

        await User.updateEmployee(employee.id, payload)
        n.success(__('User updated successfully'))
        onSuccess?.()
        if (refetch) refetch()
      } catch (error) {
        n.error(__('Failed to update user'))
      }
    }

    React.useImperativeHandle(ref, () => ({
      submit: async () => {
        const isValid = await methods.trigger()

        if (isValid) {
          setOpenConfirm(true)
        }
      },
      reset: () => {
        methods.reset()
      }
    }))

    useEffect(() => {
      if (!employee) return

      methods.reset({
        phone_code: employee?.phone_code || '+84',
        phone: employee?.phone || ''
      })
    }, [employee, methods])

    return (
      <>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Grid container spacing={12}>
              <Grid size={6} gap={2}>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography variant='body2' fontWeight={500}>
                    {__('Change Phone From')}
                  </Typography>
                </Grid>
                <Grid container sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
                  <Grid size={{ xs: 12, sm: 4 }} sx={{ flex: 1 }}>
                    <Controller
                      name='phone_code'
                      control={methods.control}
                      render={({ field }) => (
                        <PhoneCodeSelection {...field} placeholder={__('+1 (*)')} required disabled />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 7 }} sx={{ flex: 1 }} className='flex-1'>
                    <FormInput
                      name='phone'
                      placeholder={__('Enter Phone')}
                      control={methods.control}
                      required
                      fullWidth
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={6} gap={2}>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography variant='body2' fontWeight={500}>
                    {__('Change Phone To')} <span style={{ color: 'red' }}>*</span>
                  </Typography>
                </Grid>
                <Grid container sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
                  <Grid size={{ xs: 12, sm: 4 }} sx={{ flex: 1 }}>
                    <Controller
                      name='phone_code'
                      control={methods.control}
                      render={({ field }) => (
                        <PhoneCodeSelection {...field} placeholder={__('+1 (*)')} required disabled />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 7 }} sx={{ flex: 1 }} className='flex-1'>
                    <FormInput
                      name='phone_new'
                      placeholder={__('Enter new phone number')}
                      control={methods.control}
                      type='number'
                      required
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </FormProvider>

        <EmployeeConfirmPhoneDialog
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          employee={employee}
          newPhone={methods.getValues('phone_new')}
          onSuccess={() => {
            setOpenConfirm(false)
            onSuccess?.()
            if (refetch) refetch()
          }}
        />
      </>
    )
  }
)

export default EmployeeEditPhoneForm
